import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Grid, MenuItem, Select, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  useLazyGetVendorLeadTotalSummaryQuery,
  useLazyGetVendorLeadTrendsQuery,
} from "store/services/reportApi";
import VendorLeadTrendChart from "./VendorLeadTrendChart";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

// ── Constants ─────────────────────────────────────────────────────────────────
const BAR_COLOR = "#c62828";
const BAR_COLOR_NIL = "#ef9a9a"; // faint red for zero bars
const CHART_HEIGHT = 150;

// Shared target height for both cards (non-fullscreen). Tune here if the
// rendered cards are off by a few px — both components key off this.
const CARD_HEIGHT = 370;

// ── Simple Bar Chart (no chart library) ─────────────────────────────────────────
const BarGroup = ({ labels, values, isPercent = false }) => {
  const maxValue = Math.max(...values, 1);

  return (
    <Box display="flex" alignItems="flex-end" sx={{ height: CHART_HEIGHT + 52, mt: 1.5 }}>
      {values.map((value, i) => {
        const barHeight =
          value > 0 ? Math.max((value / maxValue) * CHART_HEIGHT, 6) : 3;

        return (
          <Box
            key={i}
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex={1}
            sx={{ minWidth: 0 }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: "text.primary", mb: 0.5, lineHeight: 1 }}
            >
              {isPercent ? `${value}%` : value}
            </Typography>

            <Box
              sx={{
                width: "55%",
                maxWidth: 32,
                height: barHeight,
                bgcolor: value > 0 ? BAR_COLOR : BAR_COLOR_NIL,
                borderRadius: "3px 3px 0 0",
              }}
            />

            <Typography
              sx={{ fontSize: 10, color: "text.secondary", mt: 0.75, textAlign: "center" }}
              noWrap
            >
              {labels[i]}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ── Stat Chip ─────────────────────────────────────────────────────────────────
const StatChip = ({ label, value, paletteKey = "error", shade = "main" }) => (
  <Box
    sx={{
      px: 1.5,
      py: 0.5,
      borderRadius: 1.5,
      bgcolor: (theme) => alpha(theme.palette[paletteKey][shade], 0.1),
      display: "flex",
      alignItems: "baseline",
      gap: 0.75,
    }}
  >
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: `${paletteKey}.${shade}`, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
  </Box>
);

// ── Conversion Ratio Card ────────────────────────────────────────────────────────
const ConversionRatioCard = () => {
  // Independent months selector — not synced with VendorLeadTrendChart
  const [months, setMonths] = useState(6);
  const [triggerGetVendorLeadTrends, { data: response = {}, isLoading, isError }] =
    useLazyGetVendorLeadTrendsQuery();

  useEffect(() => {
    triggerGetVendorLeadTrends({ months });
  }, [months, triggerGetVendorLeadTrends]);

  const trendData = response.data || [];
  const conversionRates = trendData.map((entry) => entry.conversionRate);
  const labels = trendData.map((entry) => entry.label);

  // Build a year-range label, e.g. "2026" or "2025 – 2026"
  const years = trendData.map((entry) => entry.year);
  const yearRange =
    years.length > 0
      ? years[0] === years[years.length - 1]
        ? `${years[0]}`
        : `${years[0]} – ${years[years.length - 1]}`
      : "";

  const summary = response.summary || { totalCreated: 0, totalWon: 0, conversionRate: 0 };
  const { totalCreated, totalWon, conversionRate: avgConversion } = summary;

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, height: CARD_HEIGHT }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, height: CARD_HEIGHT }}>
        <CardContent>
          <Typography color="error" variant="body2">Failed to load conversion data.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: CARD_HEIGHT, borderColor: "divider", overflow: "hidden" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Title row + months selector */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
              Lead Conversion Ratio
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {yearRange}
            </Typography>
          </Box>
          <Select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 130, height: 32 }}
          >
            <MenuItem value={6}>Last 6 Months</MenuItem>
            <MenuItem value={12}>Last 12 Months</MenuItem>
          </Select>
        </Box>

        {/* Subtitle + stat row */}
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.4} mb={1}>
          Won to created ratio month-wise for the last {months} months
        </Typography>
        {/* Stat chip row */}
        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
          <StatChip label="Created" value={totalCreated} paletteKey="error" />
          <StatChip label="Won" value={totalWon} paletteKey="success" />
          <StatChip label="Conversion" value={`${avgConversion}%`} paletteKey="error" shade="dark" />
        </Box>

        <BarGroup labels={labels} values={conversionRates} isPercent />
      </CardContent>
    </Card>
  );
};

// ── Main Screen ───────────────────────────────────────────────────────────────
const VendorLeadAnalyticsScreen = () => {
  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Vendor Lead Analytics" },
  ];

  const [triggerTotalSummary, { data: totalSummaryResponse }] =
    useLazyGetVendorLeadTotalSummaryQuery();

  useEffect(() => {
    triggerTotalSummary();
  }, [triggerTotalSummary]);

  const totalSummary = totalSummaryResponse?.summary || {
    totalCreated: 0,
    totalWon: 0,
    conversionRate: 0,
  };

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Paper>
        <Box p={3}>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ display: "flex", gap: 4, py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box>
                <Typography variant="h5" fontWeight={700} lineHeight={1}>
                  {totalSummary.totalCreated}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Leads Created (All Time)
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} lineHeight={1}>
                  {totalSummary.totalWon}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Leads Won (All Time)
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="error.dark" lineHeight={1}>
                  {totalSummary.conversionRate}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Overall Conversion Rate
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            {/* Lead Created vs Lead Won — full-feature line chart */}
            <Grid item xs={12} md={8}>
              <VendorLeadTrendChart />
            </Grid>

            {/* Lead Conversion Ratio — simple bar card with its own months selector */}
            <Grid item xs={12} md={4}>
              <ConversionRatioCard />
            </Grid>
          </Grid>
        </Box>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VendorLeadAnalyticsScreen;